import {
    XyDataSeries,
    SciChartSurface,
    ZoomPanModifier,
    MouseWheelZoomModifier,
    ZoomExtentsModifier,
    RolloverModifier,
    RubberBandXyZoomModifier,
    EXyDirection,
    EExecuteOn,
    FastLineRenderableSeries,
    LegendModifier,
    EAutoRange,
    NumberRange,
    EAxisAlignment,
    NumericAxis,
    DateTimeNumericAxis,
    CursorModifier,
    SeriesInfo,
    IRenderableSeries,
    } from "scichart";
  import { TInitFunction } from "scichart-react";
  import moment from "moment";
  import { axisLabelDateFormatter } from "../../../../Shared/SciChart/SmartAxis/axisLabelDateFormatter";
  import { GetChartTheme } from "../../../../Shared/SciChart/ChartConfiguration";
import { LocalizationService } from "@progress/kendo-react-intl";
import { DateTickProvider } from "../../../../Shared/SciChart/SmartAxis/DateTickProvider";
import { SmartDateTZLabelProvider } from "../../../../Shared/SciChart/SmartAxis/SmartDateTZLabelProvider";
import { createDateTzConverter } from "../../../../Shared/SciChart/SmartAxis/timezones";
  
  export type TFuelBurnConfigFunc = TInitFunction<SciChartSurface>;

  export interface Props {
    chartData: any
  }

  export interface ChartOptions {
    visibleRange: { min: number | undefined; max: number | undefined; };
    setVisibleRange: (range: any) => void;
    setVisibleSeriesFuelBurn: (series: any, isChecked: boolean) => void;
    visibleSeriesFuelBurn: { [key: string]: boolean;}
    aggregator: string;
    isSimpleChart: boolean;
    isFullscreen?: boolean;
  }
  
  export const getChartAPI = (props:Props, chartOptions:ChartOptions, localizationService: LocalizationService, currentLanguage: { locale: string; localeId: string; }) => {

    const endFilterDateUnix = moment(props.chartData.dateFilter.endDate).unix();
    const startFilterDateUnix = moment(props.chartData.dateFilter.startDate).unix();

    const theme = GetChartTheme();  

    const initChart : TFuelBurnConfigFunc = async (rootElement: string | HTMLDivElement) => {
        const {sciChartSurface, wasmContext} = await SciChartSurface.create(rootElement, {theme: theme});

      // #region Setup Tooltip Data Template
      const getTooltipDataTemplate = (seriesInfo: SeriesInfo, tooltipTitle: string, tooltipLabelX: string, tooltipLabelY: string) => {
        const lines: string[] = [];
        lines.push(tooltipTitle);
        lines.push(`x: ${seriesInfo.formattedXValue}, y: ${seriesInfo.formattedYValue}`);
        return lines;
      };
      // #endregion

      // #region Setup the XAxis
        const xAxis = new DateTimeNumericAxis(wasmContext, { 
        drawMajorBands: false, 
        drawMinorGridLines: false, 
        drawMajorGridLines: false, 
        visibleRange: new NumberRange(chartOptions.visibleRange?.min, chartOptions.visibleRange?.max),
        visibleRangeLimit: new NumberRange(startFilterDateUnix, endFilterDateUnix),
        growBy: new NumberRange(0.05, 0.05),
        labelStyle: {fontFamily:"Roboto", fontSize:12}},
        );
  
        // Overrides getDeltaFromRange to get nice major and minor deltas for the ticks
        xAxis.deltaCalculator.getDeltaFromRange = axisLabelDateFormatter.getDeltaFromRange;
  
        const updateSettings = (xAxis: NumericAxis) => {
          const timezone = moment.tz.guess();
          // Calculate ticks which respect the time zone
          xAxis.tickProvider = new DateTickProvider(wasmContext, timezone);
          // Draws nice axis labels timezone and locale specific
          xAxis.labelProvider = new SmartDateTZLabelProvider(timezone,true, currentLanguage?.localeId);
          const dateTzConverter = createDateTzConverter(timezone);
          // This is needed for Cursor and Rollover chart modifiers
          xAxis.labelProvider.formatCursorLabel = timestamp =>
              chartOptions.aggregator === 'none' ? axisLabelDateFormatter.toFullDateTime(timestamp, dateTzConverter, currentLanguage?.localeId) : axisLabelDateFormatter.toFullDateOnly(timestamp, dateTzConverter, currentLanguage?.localeId);
          };
          // Setting timezone to local system
          updateSettings(xAxis);
  
        xAxis.visibleRangeChanged.subscribe((args) => { chartOptions.setVisibleRange(args?.visibleRange); });
  
        sciChartSurface.xAxes.add(xAxis);
        // #endregion

      // #region Setup the YAxis
      // Left Y-axis for Fuel Burn (L/hr)
      const yAxisFuelBurn = new NumericAxis(wasmContext,{
        id: "fuelBurnAxis",
        axisAlignment: EAxisAlignment.Left, 
        drawMajorBands: false,
        drawMinorGridLines: false, 
        drawMajorGridLines: true, 
        labelPostfix: " L/hr", 
        labelPrecision: 0,
        autoRange: EAutoRange.Always,
        growBy: new NumberRange(0, 0.2),
        labelStyle: {fontFamily:"Roboto", fontSize:12}});
  
      // Right Y-axis for Speed (km/h)
      const yAxisSpeed = new NumericAxis(wasmContext,{
        id: "speedAxis",
        axisAlignment: EAxisAlignment.Right, 
        drawMajorBands: false, 
        drawMinorGridLines: false, 
        drawMajorGridLines: false, 
        labelPostfix:" km/h",
        autoRange: EAutoRange.Always,
        growBy: new NumberRange(0, 0.2),
        labelPrecision: 0,
        labelStyle: {fontFamily:"Roboto", fontSize:12}});
  
      // Allow negative values - removed minimum 0 constraint

      sciChartSurface.yAxes.add(yAxisFuelBurn);
      sciChartSurface.yAxes.add(yAxisSpeed);
      // #endregion

      // #region Setup the Chart Modifiers
      sciChartSurface.chartModifiers.add(
        new CursorModifier({ showAxisLabels: false,  id: "FuelBurnModifierGroup" }),
        new ZoomPanModifier({xyDirection: EXyDirection.XDirection}),
        new MouseWheelZoomModifier({xyDirection:EXyDirection.XDirection}),
        new ZoomExtentsModifier(),
        new RolloverModifier({
          hitTestRadius: 10,
          showTooltip: true,
          snapToDataPoint: true,
          showRolloverLine: false,
          tooltipDataTemplate: getTooltipDataTemplate, 
          id: "FuelBurnRollOver"
        }),
        new RubberBandXyZoomModifier({ executeOn: EExecuteOn.MouseRightButton }),
      );
  
      const legend = new LegendModifier({
        placementDivId: chartOptions.isFullscreen ? "legend-fuel-burn-fullscreen" : "legend-fuel-burn",
        showCheckboxes: true,
        showSeriesMarkers: true,
        showLegend: chartOptions.isFullscreen ? true : !chartOptions.isSimpleChart,
        // Subscribe to checked changed here
        isCheckedChangedCallback: (series: IRenderableSeries, isChecked: boolean) => {
          chartOptions.setVisibleSeriesFuelBurn(series.id, isChecked)  
        }
      });

      sciChartSurface.chartModifiers.add(legend);

      // #endregion

      // #region Setup the Series and Renderable Series
      
      // Fuel Burn series (green line, left axis)
      const timestampFuelBurn = props.chartData.timestampFuelBurn || [];
      const dataFuelBurn = props.chartData.dataFuelBurn || [];
      
      // Add series
      const fuelBurnSeries = new FastLineRenderableSeries(wasmContext, {  
        stroke: "#00A651", // Green color
        strokeThickness: 2,
        yAxisId: "fuelBurnAxis", 
        id: "FuelBurnId",
        isVisible: true
      });
      
      fuelBurnSeries.dataSeries = new XyDataSeries(wasmContext, {
        xValues: timestampFuelBurn,
        yValues: dataFuelBurn,
        dataSeriesName: "Fuel Burn"
      });

      // Apply initial visibility from chartOptions
      fuelBurnSeries.isVisible = chartOptions.visibleSeriesFuelBurn?.FuelBurnId !== false;
      
      fuelBurnSeries.rolloverModifierProps.tooltipTextColor = 'White';
      sciChartSurface.renderableSeries.add(fuelBurnSeries);

      // Speed series (blue line, right axis)
      const timestampSpeed = props.chartData.timestampSpeed || [];
      const dataSpeed = props.chartData.dataSpeed || [];
      
      const speedSeries = new FastLineRenderableSeries(wasmContext, {  
        stroke: "#0078D4", // Blue color
        strokeThickness: 2,
        yAxisId: "speedAxis", 
        id: "SpeedId",
        isVisible: true
      });
      
      speedSeries.dataSeries = new XyDataSeries(wasmContext, {
        xValues: timestampSpeed,
        yValues: dataSpeed,
        dataSeriesName: "Speed"
      });

      // Apply initial visibility from chartOptions
      speedSeries.isVisible = chartOptions.visibleSeriesFuelBurn?.SpeedId !== false;

      speedSeries.rolloverModifierProps.tooltipTextColor = 'White';
      sciChartSurface.renderableSeries.add(speedSeries);
      
      // Create a series that represents the entire filter period so the chart displays the entire range
      // Add this AFTER the data series so it doesn't interfere with autoRange calculation
      const filterPeriodSeries = new FastLineRenderableSeries(wasmContext, {
        id: "FilterPeriodSeriesId", 
        isVisible: false,
        yAxisId: "fuelBurnAxis", // Use fuelBurnAxis as default
        dataSeries: new XyDataSeries(wasmContext, {
          xValues: [startFilterDateUnix, endFilterDateUnix],
          yValues: [0, 0], 
          dataSeriesName: "filterPeriodSeries"
        })
      });  
      sciChartSurface.renderableSeries.add(filterPeriodSeries);
      legend.includeSeries(filterPeriodSeries, false);
      // #endregion

      return { sciChartSurface};
    }

    const onDelete = (initResult: any) => {
      initResult?.sciChartSurface?.delete();
    };

    return {initChart, onDelete};
  }
